import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import type { Volume } from '../data/content';
interface BonusCarouselProps {
  bonuses: Volume[];
}
const swipeThreshold = 50;
export function BonusCarousel({
  bonuses
}: BonusCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const goTo = (index: number) => {
    setDirection(index > activeIndex ? 1 : -1);
    setActiveIndex(index);
  };
  const goNext = () => {
    goTo(activeIndex === bonuses.length - 1 ? 0 : activeIndex + 1);
  };
  const goPrevious = () => {
    goTo(activeIndex === 0 ? bonuses.length - 1 : activeIndex - 1);
  };
  if (bonuses.length === 0) {
    return null;
  }
  const activeBonus = bonuses[activeIndex];
  return <section aria-label="Carrusel de bonos exclusivos" data-id="element-16">
      <div className="overflow-hidden rounded-2xl border border-gold/40 bg-navy-light/80 shadow-xl" data-id="element-17">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-18">
          <motion.article key={activeBonus.title} custom={direction} initial={{
          opacity: 0,
          x: direction >= 0 ? 44 : -44
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: direction >= 0 ? -44 : 44
        }} transition={{
          duration: 0.22,
          ease: 'easeOut'
        }} drag="x" dragConstraints={{
          left: 0,
          right: 0
        }} dragElastic={0.18} onDragEnd={(_, info) => {
          if (info.offset.x < -swipeThreshold) {
            goNext();
          }
          if (info.offset.x > swipeThreshold) {
            goPrevious();
          }
        }} className="touch-pan-y select-none" data-id="element-19">
            <div className="flex items-center justify-between px-4 pb-3 pt-4" data-id="element-20">
              <span className="rounded-full bg-gold px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-wider text-navy-deep" data-id="element-21">
                Bono {activeIndex + 1} de {bonuses.length}
              </span>
              <span className="text-2xl" aria-hidden="true" data-id="element-22">
                {activeBonus.emoji}
              </span>
            </div>

            <h3 className="min-h-[44px] px-4 text-center font-display text-xl font-extrabold uppercase leading-tight text-white" data-id="element-23">
              {activeBonus.title.replace(/^BONO \d+ — /, '')}
            </h3>

            <div className="mt-4 flex h-56 items-center justify-center border-y border-white/10 bg-white p-2" data-id="element-24">
              {activeBonus.imageUrl ? <img src={activeBonus.imageUrl} alt={`Vista previa de ${activeBonus.title}`} className="h-full w-full object-contain" draggable={false} data-id="element-25" /> : <span className="text-sm text-slate-500" data-id="element-26">
                  Vista previa no disponible
                </span>}
            </div>
          </motion.article>
        </AnimatePresence>

        <div className="flex items-center justify-between gap-3 px-4 py-4" data-id="element-27">
          <button type="button" onClick={goPrevious} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold/50 text-gold transition-colors hover:bg-gold hover:text-navy-deep focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-navy" aria-label="Ver bono anterior" data-id="element-28">
            <ChevronLeftIcon className="h-5 w-5" data-id="element-29" />
          </button>

          <div className="flex items-center gap-2" aria-label={`Bono ${activeIndex + 1} de ${bonuses.length}`} data-id="element-30">
            {bonuses.map((bonus, index) => <button key={bonus.title} type="button" onClick={() => goTo(index)} className={`h-2.5 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-navy ${index === activeIndex ? 'w-6 bg-gold' : 'w-2.5 bg-white/35 hover:bg-white/60'}`} aria-label={`Ver bono ${index + 1}`} aria-current={index === activeIndex ? 'true' : undefined} data-id="element-31" />)}
          </div>

          <button type="button" onClick={goNext} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold/50 text-gold transition-colors hover:bg-gold hover:text-navy-deep focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-navy" aria-label="Ver siguiente bono" data-id="element-32">
            <ChevronRightIcon className="h-5 w-5" data-id="element-33" />
          </button>
        </div>
      </div>
      <p className="mt-3 text-center text-xs text-slate-300" data-id="element-34">
        Deslizá para ver los 6 bonos exclusivos
      </p>
    </section>;
}